/**
 * One stored `AiInsight`, read into something a component cannot render wrongly.
 *
 * Pure: no React, no clock, no request. Every branch is drivable without mounting
 * anything, for the reason `web/src/overview/fold.ts` gives.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE ONE RULE THIS MODULE EXISTS FOR: A CLAIM NEVER TRAVELS WITHOUT ITS CAVEAT
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * An insight is a sentence somebody will act on **about another person**. The
 * qualifier that makes it safe to act on lives in `limitations`, and prose is the
 * worst possible medium for a qualifier because it is the clause a reader skims.
 *
 * G16 makes this concrete and current: a stale score now reaches the AI path, the
 * generator **proceeds and says so**, and a deterministic sentence is appended to
 * `limitations` *after validation* on the `published`, `fallback` **and** `failed`
 * paths — so the caveat is in the stored row whether or not the model complied with
 * its prompt. A screen that dropped `limitations` would delete the only place that
 * announcement survives.
 *
 * So `Caveats` is `readonly [string, ...string[]]`. Non-empty **by the type**, not by
 * a check somebody remembers to write. `openapi.yaml` says `minItems: 1` and
 * `wp_ai_limits_ck` refuses a blank one at the database — both are *server* promises,
 * and this is the client declining to depend on them for a field whose absence is the
 * defect. A row that somehow arrives with `[]` gets `#DATA_INCOMPLETE` as **text**,
 * which is the D-22 answer: never blank, never zero, never a dash.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  A FALLBACK CARRIES NO INFERENCE, SO ITS VARIANT CARRIES NO INFERENCE FIELDS
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * API_CONTRACT §8.9 lists four independent markers that tell an arithmetic summary
 * from a real analysis, and the *fourth* is that `possible_causes` and
 * `recommended_actions` are **both empty** — "the two fields that carry inference,
 * and an arithmetic summary infers nothing".
 *
 * A shape that put those two arrays on every variant would let a renderer print
 * inference a fallback never made, the day a server bug filled them. So they exist
 * only on `published`. That is `incidents/sla.ts`'s move — the breached variant
 * carries `overdueMs` and deliberately **no** remaining time — and `reports/
 * download.ts`'s, whose `DownloadState` has no `url` member in any state.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  `ai_unavailable` IS NOWHERE IN THIS TYPE, ON PURPOSE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The flag is `status === 'fallback'` and nothing else — API_CONTRACT §8.9 calls it
 * "the fifth place, a convenience for a dashboard", and says in as many words that
 * "a UI reading only this flag and then rendering a fallback in the same typeface as
 * a real insight is exactly the failure the other four exist to prevent".
 *
 * The strongest way to not do that is to make it unreachable: no variant below has an
 * `aiUnavailable` member, so no component can branch on it. The screen branches on
 * `kind`, which is `status`. What the flag *is* still good for is a **cross-check**:
 * `markersDisagree` below reports a row whose boolean and `status` contradict each
 * other, and the card renders that as an alert rather than silently picking a half.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  A BUDGET REFUSAL IS NOT AN OUTAGE AND IS NOT AN EMPTY RESULT
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `status: 'failed'` has exactly one cause today — `WORKFORCE_QUOTA_EXCEEDED` — and
 * `ai_unavailable` is **false** on it, because "billing is not availability". The
 * queued path stores such a row so the poll has something to read, and it still
 * carries the arithmetic summary. It gets its own variant here and its own panel on
 * screen, because *"no insight because we chose not to spend"* and *"no insight
 * because nothing was found"* are two different sentences to the person reading them.
 */

import type { AiInsightStatusName, AiInsightTypeName } from '../api/types';

/* ─────────────────────────────────────────────────────────── the caveat type */

/**
 * `limitations`, non-empty by construction.
 *
 * A tuple rather than `readonly string[]` so that `caveats[0]` is a `string` and not
 * `string | undefined`, and so that a future refactor cannot hand a component an
 * empty array without a compile error.
 */
export type Caveats = readonly [string, ...string[]];

/**
 * The text a missing `limitations` becomes.
 *
 * Deliberately the reports sentinel `#DATA_INCOMPLETE` rather than a new word: it is
 * the same fact one surface over — *a record exists and the record of it has a hole*
 * — and `web/src/reports/figure.ts` already teaches this build's readers what the
 * four sentinels mean. It is **text**, never a number and never an empty region, and
 * the card renders a Vietnamese explanation beside it.
 */
export const CAVEAT_ABSENT_SENTINEL = '#DATA_INCOMPLETE';

/** Is this caveat line the client's own substitute rather than the server's text? */
export function isCaveatSentinel(line: string): boolean {
  return line === CAVEAT_ABSENT_SENTINEL;
}

/**
 * `limitations` off the wire, as a non-empty tuple.
 *
 * Takes `unknown` for `readFreshness`'s reason: a declared `string[]` would let the
 * type system promise a shape the wire cannot promise, and the one thing this field
 * must never do is read as "there were no limitations".
 *
 * Blank and whitespace-only entries are dropped **before** the emptiness decision, so
 * a `limitations: ['   ']` is an absence and not a caveat made of one space —
 * rendering that would be a blank region that looks like a considered answer.
 */
export function readCaveats(value: unknown): Caveats {
  const lines = Array.isArray(value)
    ? value
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '')
    : [];
  const [first, ...rest] = lines;
  return first === undefined ? [CAVEAT_ABSENT_SENTINEL] : [first, ...rest];
}

/* ────────────────────────────────────────────────────────── the confidence type */

/**
 * `confidence`, or the reason there is not one.
 *
 * The same union `reports/figure.ts` builds for `row_count`, and for the same reason
 * D-22 gives: **a legitimate zero exists here.** A fallback's confidence really is
 * `0` — WP-M2 sets it, because an arithmetic summary claims nothing — so "falsy means
 * missing" is wrong in the most dangerous direction available. The `absent` variant
 * therefore carries **no numeric member at all**: a component that wanted to print
 * `0%` for an unpublished confidence would have to invent the zero where a reviewer
 * would see it being invented.
 */
export type ConfidenceAbsence = 'not_published' | 'not_a_number' | 'out_of_range';

export type Confidence =
  | { readonly kind: 'measured'; readonly value: number }
  | { readonly kind: 'absent'; readonly absence: ConfidenceAbsence };

/**
 * `Number.isFinite` decides, never truthiness, and the published range is enforced.
 *
 * `openapi.yaml` gives `minimum: 0` and `maximum: 1`. A value outside that is not a
 * confidence this client understands, and clamping it would present a server fault as
 * a reading — so it is an absence with its own reason.
 */
export function readConfidence(value: unknown): Confidence {
  if (value === undefined || value === null) return { kind: 'absent', absence: 'not_published' };
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return { kind: 'absent', absence: 'not_a_number' };
  }
  if (value < 0 || value > 1) return { kind: 'absent', absence: 'out_of_range' };
  return { kind: 'measured', value };
}

/* ───────────────────────────────────────────────────────────── the reading */

/** Facts every variant carries, whatever happened to the generation attempt. */
interface InsightCommon {
  readonly id: string;
  readonly insightType: AiInsightTypeName | null;
  /** The model's own sentence, or the deterministic arithmetic one. Never rewritten. */
  readonly headline: string;
  readonly caveats: Caveats;
  readonly confidence: Confidence;
  readonly evidence: readonly EvidenceRef[];
  readonly model: string | null;
  readonly generatedAt: string | null;
  readonly question: string | null;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
  readonly jobId: string | null;
}

export interface EvidenceRef {
  readonly evidenceId: string;
  readonly refKind: string;
  readonly ref: string;
  readonly periodStart: string | null;
  readonly periodEnd: string | null;
}

/**
 * What a card renders.
 *
 * `published` is the only variant with `causes` and `actions`, for the reason in this
 * file's header. `fallback` and `budgetRefused` carry `diagnosis` — the
 * machine-readable `fallback_reason` token — which `published` has no use for.
 */
export type InsightReading =
  | ({
      readonly kind: 'published';
      readonly causes: readonly string[];
      readonly actions: readonly string[];
    } & InsightCommon)
  | ({ readonly kind: 'fallback'; readonly diagnosis: string | null } & InsightCommon)
  | ({ readonly kind: 'budgetRefused'; readonly diagnosis: string | null } & InsightCommon)
  | ({ readonly kind: 'superseded' } & InsightCommon)
  | {
      readonly kind: 'unreadable';
      readonly reason: 'no_status' | 'unknown_status' | 'no_summary';
      readonly caveats: Caveats;
      readonly rawStatus: string | null;
    };

const INSIGHT_STATUSES: readonly AiInsightStatusName[] = [
  'published',
  'failed',
  'fallback',
  'superseded',
];

const INSIGHT_TYPES: readonly AiInsightTypeName[] = [
  'summary',
  'trend_comparison',
  'anomaly',
  'cause_hypothesis',
  'recommendation',
  'workload_analysis',
  'scoped_query',
];

function strings(value: unknown): readonly string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : [];
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function readEvidence(value: unknown): readonly EvidenceRef[] {
  if (!Array.isArray(value)) return [];
  const refs: EvidenceRef[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const evidenceId = record.evidence_id;
    const refKind = record.kind;
    const ref = record.ref;
    if (typeof evidenceId !== 'string' || typeof refKind !== 'string' || typeof ref !== 'string') {
      continue;
    }
    refs.push({
      evidenceId,
      refKind,
      ref,
      periodStart: nullableString(record.period_start),
      periodEnd: nullableString(record.period_end),
    });
  }
  return refs;
}

/**
 * The wire row, read.
 *
 * Fails closed, in `readFreshness`'s direction and for its reason: an unrecognised
 * `status` is **not** treated as `published`. Reading an unknown state as the good
 * one is the defect itself rather than a smaller version of it — it would print a
 * model's paragraph about somebody's work under a green tick because this build is a
 * version behind. `unreadable` still carries `caveats`, because the caveat is the one
 * thing that must survive every branch.
 */
export function readInsight(row: Readonly<Record<string, unknown>>): InsightReading {
  const caveats = readCaveats(row.limitations);
  const rawStatus = typeof row.status === 'string' ? row.status : null;
  if (rawStatus === null) return { kind: 'unreadable', reason: 'no_status', caveats, rawStatus };
  if (!(INSIGHT_STATUSES as readonly string[]).includes(rawStatus)) {
    return { kind: 'unreadable', reason: 'unknown_status', caveats, rawStatus };
  }
  const headline = typeof row.summary === 'string' ? row.summary.trim() : '';
  if (headline === '') {
    // A blank summary under a `published` status is not an insight with nothing to
    // say; it is a row this client cannot render honestly. An empty paragraph on this
    // screen reads as "the model looked and found nothing worth saying", which is a
    // claim nobody made.
    return { kind: 'unreadable', reason: 'no_summary', caveats, rawStatus };
  }

  const insightType = typeof row.insight_type === 'string' ? row.insight_type : null;
  const common: InsightCommon = {
    id: typeof row.id === 'string' ? row.id : '',
    insightType:
      insightType !== null && (INSIGHT_TYPES as readonly string[]).includes(insightType)
        ? (insightType as AiInsightTypeName)
        : null,
    headline,
    caveats,
    confidence: readConfidence(row.confidence),
    evidence: readEvidence(row.evidence),
    model: nullableString(row.model),
    generatedAt: nullableString(row.generated_at),
    question: nullableString(row.question),
    periodStart: nullableString(row.period_start),
    periodEnd: nullableString(row.period_end),
    jobId: nullableString(row.job_id),
  };
  const diagnosis = nullableString(row.fallback_reason);

  switch (rawStatus as AiInsightStatusName) {
    case 'published':
      return {
        kind: 'published',
        ...common,
        causes: strings(row.possible_causes),
        actions: strings(row.recommended_actions),
      };
    case 'fallback':
      return { kind: 'fallback', ...common, diagnosis };
    case 'failed':
      // Today `failed` has exactly one cause — the monthly budget — and
      // `API_CONTRACT.md` §8.9 says so. The variant is named for the *meaning* rather
      // than for the column value, because "failed" on screen is the sentence this
      // whole branch exists to avoid: nothing failed, somebody's limit was reached.
      return { kind: 'budgetRefused', ...common, diagnosis };
    case 'superseded':
      return { kind: 'superseded', ...common };
  }
}

/**
 * Does the row's `ai_unavailable` flag agree with its `status`?
 *
 * The contract makes the flag exactly `status === 'fallback'`, so a disagreement is a
 * server this client should not quietly pick a half of. It is reported rather than
 * resolved: the screen renders an alert naming both values, which is what a reader
 * needs in order to distrust the row in front of them.
 *
 * An **absent** flag is not a disagreement. It is `required` on the wire, but a body
 * that omitted it has said nothing about availability, and inventing a contradiction
 * out of a silence would be this client manufacturing a fault.
 */
export function markersDisagree(row: Readonly<Record<string, unknown>>): boolean {
  const flag = row.ai_unavailable;
  if (typeof flag !== 'boolean') return false;
  return flag !== (row.status === 'fallback');
}

/**
 * Does this insight cite anything at all?
 *
 * Spec 02 §19 step 8 makes every claim traceable to the aggregate behind it, so a
 * `published` row citing nothing is a paragraph about people with no stated basis.
 * That is worth a sentence on the card rather than an empty evidence list, which
 * reads as a rendering that did not finish.
 */
export function citesNothing(reading: InsightReading): boolean {
  return reading.kind !== 'unreadable' && reading.evidence.length === 0;
}

/**
 * A chat turn, in the shape a stored insight has.
 *
 * `AiChatResponse` is the same thing under different key names — `answer` for
 * `summary`, `insight_id` for `id` — and it carries every field the card needs:
 * `status`, `fallback_reason`, `confidence`, `limitations`, `evidence`, `model`, and
 * the two inference arrays.
 *
 * The mapping exists so that a chat answer is rendered by `InsightCard` and not by a
 * second, simpler component. That is not tidiness: a bespoke answer renderer is
 * exactly where somebody would leave `limitations` out, because a chat bubble "wants"
 * to be one paragraph. One door, one guarantee.
 *
 * `generated_at` is deliberately **not** invented here. The response publishes none,
 * and a client clock stamped onto a model's answer would be a provenance claim this
 * screen cannot support — the card simply omits the field.
 */
export function insightRowFromChat(
  response: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  return {
    id: typeof response.insight_id === 'string' ? response.insight_id : '',
    insight_type: 'scoped_query',
    summary: response.answer,
    limitations: response.limitations,
    confidence: response.confidence,
    evidence: response.evidence,
    model: response.model,
    status: response.status,
    fallback_reason: response.fallback_reason ?? null,
    ai_unavailable: response.ai_unavailable,
    possible_causes: response.possible_causes,
    recommended_actions: response.recommended_actions,
  };
}
